import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import LogSheetRenderer from '../../components/LogSheetRenderer';
import './LogSheetDrawer.css';

const LogSheetDrawer = ({ 
    logSheets, 
    tripMeta, 
    pickupLocation,
    dropoffLocation,
    currentLogIndex, 
    setCurrentLogIndex, 
    userProfile,
    isDrawerOpen,
    setIsDrawerOpen 
}) => {
    const currentLog = logSheets[currentLogIndex];
    const logSheetRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // PDF Export using html2canvas + jsPDF
    const handleExportPDF = async (e) => {
        e.stopPropagation();
        
        if (!logSheetRef.current) return;
        
        setIsExporting(true);
        
        try {
            // Dynamically import libraries
            const html2canvas = (await import('html2canvas')).default;
            const { jsPDF } = await import('jspdf');
            
            // Get the log sheet element
            const element = logSheetRef.current;
            
            // Create high-quality canvas
            const canvas = await html2canvas(element, {
                scale: 2, // Higher resolution
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
            });
            
            // Calculate PDF dimensions (Letter size landscape)
            const imgWidth = 10;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Create PDF in landscape
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'in',
                format: 'letter'
            });
            
            // Add image to PDF with margins
            const imgData = canvas.toDataURL('image/png');
            pdf.addImage(imgData, 'PNG', 0.5, 0.5, imgWidth, imgHeight);
            
            // Generate filename with date
            const dateStr = currentLog?.log_date || new Date().toISOString().split('T')[0];
            const driverName = userProfile?.driverName?.replace(/\s+/g, '_') || 'Driver';
            const filename = `ELD_Log_${driverName}_${dateStr}.pdf`;
            
            // Download PDF
            pdf.save(filename);
            
        } catch (error) {
            console.error('PDF Export Error:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Export all log sheets as multi-page PDF
    const handleExportAllPDF = async (e) => {
        e.stopPropagation();
        
        if (logSheets.length === 0) return;
        
        setIsExporting(true);
        const originalIndex = currentLogIndex;
        
        try {
            const html2canvas = (await import('html2canvas')).default;
            const { jsPDF } = await import('jspdf');
            
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'in',
                format: 'letter'
            });
            
            // Capture each log sheet
            for (let i = 0; i < logSheets.length; i++) {
                setCurrentLogIndex(i);
                
                // Wait for render
                await new Promise(resolve => setTimeout(resolve, 400));
                
                if (logSheetRef.current) {
                    const canvas = await html2canvas(logSheetRef.current, {
                        scale: 2,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#ffffff',
                        logging: false,
                    });
                    
                    const imgWidth = 10;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;
                    const imgData = canvas.toDataURL('image/png');
                    
                    if (i > 0) {
                        pdf.addPage();
                    }
                    
                    pdf.addImage(imgData, 'PNG', 0.5, 0.5, imgWidth, imgHeight);
                }
            }
            
            // Restore original index
            setCurrentLogIndex(originalIndex);
            
            const driverName = userProfile?.driverName?.replace(/\s+/g, '_') || 'Driver';
            const filename = `ELD_Logs_${driverName}_Complete.pdf`;
            pdf.save(filename);
            
        } catch (error) {
            console.error('PDF Export Error:', error);
            alert('Failed to export PDF. Please try again.');
            setCurrentLogIndex(originalIndex);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <motion.div 
            className={`log-drawer-container ${isDrawerOpen ? 'open' : 'closed'}`}
            layout
            initial={{ y: "calc(100% - 60px)" }} // Initial peek
            animate={{ 
                y: isDrawerOpen ? "0%" : "calc(100% - 60px)" 
            }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={!isDrawerOpen ? toggleDrawer : undefined}
        >
            {/* Handle / Header Area - Always Visible (Peek) */}
            <div 
                className="drawer-handle-area" 
                onClick={isDrawerOpen ? toggleDrawer : undefined} // Allow clicking handle to close
            >
                <div className="drawer-pill"></div>
                <div className="drawer-title">
                    <span className="material-symbols-outlined">description</span>
                    <span>Daily Log Sheet &bull; {currentLog?.date || 'Today'}</span>
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="drawer-content">
                {/* Navigation & Controls */}
                <div className="drawer-controls">
                    <div className="log-nav">
                        <button 
                            disabled={currentLogIndex === 0}
                            onClick={(e) => { e.stopPropagation(); setCurrentLogIndex(prev => prev - 1); }}
                        >
                            <span className="material-symbols-outlined">chevron_left</span>
                            Previous Day
                        </button>
                        <span className="log-date">{currentLog?.date}</span>
                        <button 
                            disabled={currentLogIndex === logSheets.length - 1}
                            onClick={(e) => { e.stopPropagation(); setCurrentLogIndex(prev => prev + 1); }}
                        >
                            Next Day
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>

                    <button 
                        className="print-btn"
                        onClick={handleExportPDF}
                        disabled={isExporting}
                    >
                        <span className="material-symbols-outlined">
                            {isExporting ? 'hourglass_empty' : 'picture_as_pdf'}
                        </span>
                        {isExporting ? 'Exporting...' : 'Download PDF'}
                    </button>
                    
                    {logSheets.length > 1 && (
                        <button 
                            className="print-btn secondary"
                            onClick={handleExportAllPDF}
                            disabled={isExporting}
                            title="Export all days as one PDF"
                        >
                            <span className="material-symbols-outlined">file_copy</span>
                            All Days
                        </button>
                    )}
                </div>

                {/* The Actual Graph */}
                <div className="log-renderer-wrapper" ref={logSheetRef} onClick={(e) => e.stopPropagation()}>
                    <LogSheetRenderer 
                        logSheet={currentLog} 
                        userProfile={userProfile} 
                        tripMeta={tripMeta}
                        pickupLocation={pickupLocation}
                        dropoffLocation={dropoffLocation}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default LogSheetDrawer;
